import React, { FC, ReactElement, useContext, useEffect, useMemo } from 'react'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Table from '@material-ui/core/Table'
import TableCell from '@material-ui/core/TableCell'
import TableBody from '@material-ui/core/TableBody'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { RouterCheckbox } from '../../Atoms/Inputs/RouterCheckbox'
import IconButton, { IconButtonType } from '../../../../React/Common/Components/IconButton'
import { IconName } from '../../../../React/Common/Components/Icon'
import { OutlinedSelect } from '../../Atoms/Inputs/OutlinedSelect'
import { useConfig } from '../../../../React/Common/Hooks/useConfig'
import {
    ArrayField,
    useFieldArray,
    UseFieldArrayMethods,
    useFormContext,
    UseFormMethods,
    useWatch
} from '../../../../Vendor/react-hook-form/dist/index.ie11'
import { ConfigurableTableConfig, ConfigurableTableConfigBase, defaultTypes, FormTypes } from './Types/Types'
import {
    getIndexedArrayFieldName,
    getIndexedValueFieldName,
    getItemFieldName,
    getValueFieldName
} from './Constants/FieldNames'
import { SelectOptions } from '../../../../React/Common/Components/Selects'
import { SelectOption } from '../../../../React/Common/Components/Selects/Types'
import { IsVarEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { OutlinedTextInput } from '../../Atoms/Inputs/OutlinedTextInput'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext } from '../../../../React/Common/Contexts'
import { ValidationErrors } from '../../../../React/Common/Components/ValidationErrors/ValidationErrors'
import { buildValidation } from './Validation/Validation'
import { Rules } from '../../../../React/Common/Types/Types'
import { ArrayInputField } from '../../Atoms/ArrayField/ArrayInputField'
import { OutlinedPasswordTextInput } from '../../Atoms/Inputs/OutlinedPasswordTextInput'
import { OutlineTextAreaMulti } from '../../Atoms/Inputs/OutlineTextAreaMulti'
import { ErrorLanguage } from '../../../../../i18n'

export type ConfigurableTableFieldFormLocalisation = {
    header: {
        item: string
        default?: string
        value: string
    }
}

export interface FormEntry {
    item: string
    default?: boolean | string | number | string[]
    value: boolean | string | number | string[] | Record<string, string> | Record<string, string>[]
}

export type Props =
    | {
          namespacingFunction: (name: string) => string
          localisationFunction: (name: string) => string
          localisation: ConfigurableTableFieldFormLocalisation
          config: [string, string]
          inputConfig?: undefined
          arrayFieldName: string
          currentTelSysType?: string
          isViewOnly?: boolean
          allowAdd?: boolean
          allowRemove?: boolean
          showDefaults?: boolean
          addAll?: boolean
          isAdding?: boolean
      }
    | {
          namespacingFunction: (name: string) => string
          localisationFunction: (name: string) => string
          localisation: ConfigurableTableFieldFormLocalisation
          config?: undefined
          inputConfig: Nullable<ConfigurableTableConfig[]>
          arrayFieldName: string
          currentTelSysType?: string
          isViewOnly?: boolean
          allowAdd?: boolean
          allowRemove?: boolean
          showDefaults?: boolean
          addAll?: boolean
          isAdding?: boolean
      }

export const ConfigurableTableFieldForm: FC<Props> = ({
    namespacingFunction,
    localisationFunction,
    localisation,
    config: yamlConfig,
    inputConfig,
    arrayFieldName,
    currentTelSysType,
    isViewOnly,
    allowAdd,
    allowRemove,
    showDefaults,
    isAdding = false
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(ErrorLanguage.NAME)
    const arrayFieldNamespaced: string = useMemo(
        (): string => namespacingFunction(arrayFieldName),
        [namespacingFunction, arrayFieldName]
    )

    const { control, setValue, watch }: UseFormMethods = useFormContext()
    const { append, remove, fields }: UseFieldArrayMethods<FormEntry> = useFieldArray({
        control,
        name: arrayFieldNamespaced
    })

    let config: Nullable<ConfigurableTableConfig[]> = null

    if (isVarNotEmpty(yamlConfig)) {
        const [fileName, configPath]: [string, string] = yamlConfig
        config = useConfig(fileName, configPath)
    } else {
        config = inputConfig
    }

    const configKeyed: Nullable<Record<string, ConfigurableTableConfig>> = useMemo(
        (): Nullable<Record<string, ConfigurableTableConfig>> =>
            config
                ?.filter((item: ConfigurableTableConfig): boolean =>
                    IsVarEmpty(item?.telsysTypes)
                        ? true
                        : IsVarEmpty(currentTelSysType) || item?.telsysTypes?.includes(currentTelSysType)
                )
                ?.reduce(
                    (
                        acc: Record<string, ConfigurableTableConfig>,
                        item: ConfigurableTableConfig
                    ): Record<string, ConfigurableTableConfig> => {
                        acc[item.name] = item
                        return acc
                    },
                    {}
                ),
        [JSON.stringify(config), currentTelSysType]
    )

    const options: Nullable<SelectOptions<string>> = useMemo(
        (): Nullable<SelectOptions<string>> =>
            Object.values(configKeyed ?? {})?.map(
                (item: ConfigurableTableConfig): SelectOption<string> => ({
                    value: item.name,
                    label:
                        item?.label?.[language.getLanguageID()] ??
                        item?.label?.[Object.keys(item?.label)?.[0]] ??
                        localisationFunction(item?.name ?? EMPTY_STRING)
                })
            ),
        [configKeyed]
    )

    const arrayFieldValues: Definable<FormEntry[]> = useWatch({
        name: arrayFieldNamespaced,
        defaultValue: []
    })

    const newItemValue: Definable<string> = useWatch({
        name: getItemFieldName(namespacingFunction)
    })

    const newItemConfig: Nullable<ConfigurableTableConfig> = useMemo(
        (): Nullable<ConfigurableTableConfig> => (newItemValue ? configKeyed?.[newItemValue] : null),
        [newItemValue, configKeyed]
    )

    const finalOptions: Nullable<SelectOptions<string>> = useMemo(
        (): Nullable<SelectOptions<string>> =>
            options?.filter(
                (option: SelectOption<string>): boolean =>
                    !arrayFieldValues?.some((field: FormEntry): boolean => field.item === option.value)
            ),
        [JSON.stringify(options), JSON.stringify(arrayFieldValues)]
    )

    const outputDefaultValues = (
        type: ConfigurableTableConfigBase['type'],
        defaults: string | number | boolean | { value: string }[]
    ): ReactElement => {
        switch (type) {
            case FormTypes.BOOLEAN:
                return <RouterCheckbox disabled isChecked={!!defaults} />
            case FormTypes.ARRAY:
                return Array.isArray(defaults) ? (
                    <>
                        {defaults.map(
                            (item: { value: string }, index: number): ReactElement => (
                                <div key={`${item.value}-${index}`}>{item.value}</div>
                            )
                        )}
                    </>
                ) : (
                    <></>
                )
            default:
                return <span>{defaults}</span>
        }
    }

    useEffect((): void => {
        if (newItemConfig) {
            if (newItemConfig?.type === FormTypes.ARRAY) {
                setValue(
                    getValueFieldName(namespacingFunction),
                    newItemConfig?.default?.map((value: Record<string, string>) => ({ value }))
                )
            } else {
                setValue(getValueFieldName(namespacingFunction), newItemConfig?.default)
            }
        }
    }, [newItemConfig])

    useEffect((): void => {
        if (isVarNotEmpty(finalOptions) && finalOptions?.length > 0) {
            setValue(getItemFieldName(namespacingFunction), finalOptions[0].value)
        }
    }, [JSON.stringify(finalOptions)])

    useEffect((): void => {
        if (!inputConfig?.length || !isAdding) return

        remove(fields.map((_: Partial<ArrayField>, i: number): number => i))

        inputConfig.forEach((conf: ConfigurableTableConfig): void =>
            append({
                item: conf.name,
                value: conf.default ?? EMPTY_STRING
            })
        )
    }, [inputConfig, fields.length])

    return (
        <div>
            <Table className='configurable-table table-head-left'>
                <TableHead>
                    <TableRow>
                        <TableCell>{localisation.header.item}</TableCell>
                        {showDefaults ? <TableCell>{localisation.header.default}</TableCell> : <></>}
                        <TableCell>{localisation.header.value}</TableCell>
                        {allowAdd || allowRemove ? <TableCell /> : <></>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {fields.map((field: Partial<ArrayField>, index: number): ReactElement => {
                        const formEntry: Nullable<FormEntry> = watch(
                            getIndexedArrayFieldName(arrayFieldName, index, namespacingFunction)
                        )
                        const currentPolicyConfig: Nullable<ConfigurableTableConfig> =
                            configKeyed?.[formEntry?.item ?? EMPTY_STRING]

                        const rules: Rules = { validate: buildValidation(currentPolicyConfig?.validations, language) }

                        const defaultValue: defaultTypes =
                            currentPolicyConfig?.type === FormTypes.SELECT
                                ? currentPolicyConfig?.options?.find(
                                      (option: SelectOption<string>): boolean =>
                                          option.value === currentPolicyConfig?.default
                                  )?.label ?? currentPolicyConfig?.default
                                : currentPolicyConfig?.default

                        const label: string =
                            currentPolicyConfig?.label?.[language.getLanguageID()] ??
                            currentPolicyConfig?.label?.[Object.keys(currentPolicyConfig?.label)?.[0]] ??
                            localisationFunction(currentPolicyConfig?.name ?? EMPTY_STRING) ??
                            currentPolicyConfig?.name ??
                            EMPTY_STRING

                        return (
                            <TableRow key={`${field.value}-${index}`}>
                                <TableCell>{label}</TableCell>
                                {showDefaults ? (
                                    <TableCell>
                                        {outputDefaultValues(
                                            currentPolicyConfig?.type ?? FormTypes.STRING,
                                            defaultValue ?? EMPTY_STRING
                                        )}
                                    </TableCell>
                                ) : (
                                    <></>
                                )}
                                <TableCell>
                                    {currentPolicyConfig?.type === FormTypes.BOOLEAN ? (
                                        <ValidationErrors
                                            name={getIndexedValueFieldName(arrayFieldName, index, namespacingFunction)}
                                        >
                                            <RouterCheckbox
                                                disabled={isViewOnly}
                                                name={getIndexedValueFieldName(
                                                    arrayFieldName,
                                                    index,
                                                    namespacingFunction
                                                )}
                                                rules={rules}
                                            />
                                        </ValidationErrors>
                                    ) : (
                                        <></>
                                    )}
                                    {currentPolicyConfig?.type === FormTypes.INTEGER ||
                                    currentPolicyConfig?.type === FormTypes.NUMBER ? (
                                        <ValidationErrors
                                            name={getIndexedValueFieldName(arrayFieldName, index, namespacingFunction)}
                                        >
                                            <OutlinedTextInput
                                                disabled={isViewOnly}
                                                type='number'
                                                inputName={getIndexedValueFieldName(
                                                    arrayFieldName,
                                                    index,
                                                    namespacingFunction
                                                )}
                                                rules={rules}
                                            />
                                        </ValidationErrors>
                                    ) : (
                                        <></>
                                    )}
                                    {currentPolicyConfig?.type === FormTypes.STRING ? (
                                        <ValidationErrors
                                            name={getIndexedValueFieldName(arrayFieldName, index, namespacingFunction)}
                                        >
                                            <OutlinedTextInput
                                                disabled={isViewOnly}
                                                inputName={getIndexedValueFieldName(
                                                    arrayFieldName,
                                                    index,
                                                    namespacingFunction
                                                )}
                                                rules={rules}
                                            />
                                        </ValidationErrors>
                                    ) : (
                                        <></>
                                    )}
                                    {currentPolicyConfig?.type === FormTypes.SELECT ? (
                                        <ValidationErrors
                                            name={getIndexedValueFieldName(arrayFieldName, index, namespacingFunction)}
                                        >
                                            <OutlinedSelect
                                                disabled={isViewOnly}
                                                options={currentPolicyConfig?.options ?? []}
                                                name={getIndexedValueFieldName(
                                                    arrayFieldName,
                                                    index,
                                                    namespacingFunction
                                                )}
                                                rules={rules}
                                            />
                                        </ValidationErrors>
                                    ) : (
                                        <></>
                                    )}
                                    {currentPolicyConfig?.type === FormTypes.ARRAY ? (
                                        <ArrayInputField
                                            name={getIndexedValueFieldName(arrayFieldName, index, namespacingFunction)}
                                            language={language}
                                            unique={currentPolicyConfig?.validations?.uniqueItems}
                                            rules={rules}
                                            viewOnly={isViewOnly}
                                        />
                                    ) : (
                                        <></>
                                    )}
                                    {currentPolicyConfig?.type === FormTypes.PASSWORD ? (
                                        <ValidationErrors
                                            name={getIndexedValueFieldName(arrayFieldName, index, namespacingFunction)}
                                        >
                                            <OutlinedPasswordTextInput
                                                disabled={isViewOnly}
                                                inputName={getIndexedValueFieldName(
                                                    arrayFieldName,
                                                    index,
                                                    namespacingFunction
                                                )}
                                                alwaysShowPasswordVisibilityOption={!isViewOnly}
                                                rules={rules}
                                            />
                                        </ValidationErrors>
                                    ) : (
                                        <></>
                                    )}
                                    {currentPolicyConfig?.type === FormTypes.MULTILINE ? (
                                        <ValidationErrors
                                            name={getIndexedValueFieldName(arrayFieldName, index, namespacingFunction)}
                                        >
                                            <OutlineTextAreaMulti
                                                disabled={isViewOnly}
                                                name={getIndexedValueFieldName(
                                                    arrayFieldName,
                                                    index,
                                                    namespacingFunction
                                                )}
                                            />
                                        </ValidationErrors>
                                    ) : (
                                        <></>
                                    )}
                                </TableCell>
                                {allowRemove ? (
                                    <TableCell>
                                        {isViewOnly ? (
                                            <></>
                                        ) : (
                                            <div className='icon-button-group'>
                                                <IconButton
                                                    iconName={IconName.X}
                                                    type={IconButtonType.BUTTON}
                                                    onClick={(): void => remove(index)}
                                                />
                                            </div>
                                        )}
                                    </TableCell>
                                ) : (
                                    <></>
                                )}
                            </TableRow>
                        )
                    })}
                    {isVarNotEmpty(finalOptions) && finalOptions?.length > 0 && allowAdd ? (
                        <TableRow className='adding-row'>
                            <TableCell>
                                <OutlinedSelect options={finalOptions} name={getItemFieldName(namespacingFunction)} />
                            </TableCell>
                            {showDefaults ? (
                                <TableCell>
                                    {outputDefaultValues(
                                        newItemConfig?.type ?? FormTypes.STRING,
                                        (newItemConfig?.type === FormTypes.SELECT
                                            ? newItemConfig?.options?.find(
                                                  (option: SelectOption<string>): boolean =>
                                                      option.value === newItemConfig?.default
                                              )?.label ?? newItemConfig?.default
                                            : newItemConfig?.default) ?? EMPTY_STRING
                                    )}
                                </TableCell>
                            ) : (
                                <></>
                            )}
                            <TableCell>
                                {newItemConfig?.type === FormTypes.BOOLEAN ? (
                                    <RouterCheckbox
                                        disabled={isViewOnly}
                                        name={getValueFieldName(namespacingFunction)}
                                    />
                                ) : (
                                    <></>
                                )}
                                {newItemConfig?.type === FormTypes.INTEGER ? (
                                    <OutlinedTextInput
                                        disabled={isViewOnly}
                                        type='number'
                                        inputName={getValueFieldName(namespacingFunction)}
                                    />
                                ) : (
                                    <></>
                                )}
                                {newItemConfig?.type === FormTypes.STRING ? (
                                    <OutlinedTextInput
                                        disabled={isViewOnly}
                                        inputName={getValueFieldName(namespacingFunction)}
                                    />
                                ) : (
                                    <></>
                                )}
                                {newItemConfig?.type === FormTypes.SELECT ? (
                                    <OutlinedSelect
                                        disabled={isViewOnly}
                                        options={newItemConfig?.options ?? []}
                                        name={getValueFieldName(namespacingFunction)}
                                    />
                                ) : (
                                    <></>
                                )}
                                {newItemConfig?.type === FormTypes.ARRAY ? (
                                    <ArrayInputField
                                        name={getValueFieldName(namespacingFunction)}
                                        unique={newItemConfig?.validations?.uniqueItems}
                                        language={language}
                                    />
                                ) : (
                                    <></>
                                )}
                            </TableCell>
                            <TableCell>
                                {isViewOnly ? (
                                    <></>
                                ) : (
                                    <div className='icon-button-group'>
                                        <IconButton
                                            iconName={IconName.CHECK}
                                            type={IconButtonType.BUTTON}
                                            onClick={(): void => {
                                                if (newItemConfig) {
                                                    append({
                                                        item: watch(getItemFieldName(namespacingFunction)),
                                                        default: !!newItemConfig?.default,
                                                        value: watch(getValueFieldName(namespacingFunction))
                                                    })
                                                }
                                            }}
                                        />
                                        <IconButton
                                            iconName={IconName.X}
                                            type={IconButtonType.BUTTON}
                                            onClick={(): void => {
                                                setValue(
                                                    getItemFieldName(namespacingFunction),
                                                    finalOptions?.[0]?.value
                                                )
                                            }}
                                        />
                                    </div>
                                )}
                            </TableCell>
                        </TableRow>
                    ) : (
                        <></>
                    )}
                </TableBody>
            </Table>
        </div>
    )
}
